<section
  class="flex h-full w-full flex-col overflow-hidden bg-white text-gray-900"
  role="dialog"
  [attr.aria-label]="'invite_employees' | translate"
>
  <header class="flex shrink-0 items-center gap-3 border-b border-gray-100 px-4 py-3">
    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
      <app-icon>group_add</app-icon>
    </div>
    <div class="min-w-0 flex-1">
      <h2 class="m-0 text-base font-bold leading-6 text-gray-900">{{ 'invite_employees' | translate }}</h2>
    </div>
    <button
      type="button"
      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
      [attr.aria-label]="'close' | translate"
      (click)="onCancel()"
    >
      <app-icon>close</app-icon>
    </button>
  </header>

  <div class="flex shrink-0 flex-col gap-1 border-b border-gray-100 px-4 py-3">
    <div class="flex flex-wrap items-center gap-x-6 gap-y-1">
      <app-checkbox [formControl]="isRegister" [disabled]="isSubmitting">
        {{ 'invite_auto_register' | translate }}
      </app-checkbox>
      <app-checkbox [formControl]="isSkipEmail" [disabled]="isSubmitting">
        {{ 'invite_skip_email' | translate }}
      </app-checkbox>
    </div>
    @if (isRegister.value && isSkipEmail.value) {
      <div class="flex items-center gap-2 text-sm text-amber-600">
        <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">warning_amber</app-icon>
        {{ 'invite_skip_email_register_warning' | translate }}
      </div>
    }
  </div>

  <div class="crm-scroll min-h-0 flex-1 overflow-auto px-4 py-4">
    <div class="flex min-w-[1200px] flex-col gap-2">
      <div
        class="grid grid-cols-[32px_minmax(0,1.4fr)_minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_36px] items-center gap-3 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500"
      >
        <span>#</span>
        <span>{{ 'email' | translate }} <span class="text-red-500">*</span></span>
        <span>{{ 'phone' | translate }} <span class="text-red-500">*</span></span>
        <span>{{ 'first_name' | translate }} <span class="text-red-500">*</span></span>
        <span>{{ 'last_name' | translate }} <span class="text-red-500">*</span></span>
        <span>{{ 'role' | translate }} <span class="text-red-500">*</span></span>
        <span></span>
      </div>

      @for (row of rows.controls; track row; let i = $index) {
        <div
          class="flex flex-col gap-2 rounded-xl border bg-white px-3 py-2"
          [ngClass]="rowErrors.has(row) ? 'border-red-300 bg-red-50/40' : 'border-gray-200'"
        >
          <div
            class="grid grid-cols-[32px_minmax(0,1.4fr)_minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_36px] items-start gap-3"
          >
            <span class="flex h-10 items-center text-sm font-semibold text-gray-500">{{ i + 1 }}</span>

            <app-input
              [formControl]="row.controls.email"
              [placeholder]="'enter_your_e-mail' | translate"
              [invalid]="isInvalid(row, 'email')"
              [showErrors]="false"
            />

            <app-phone-field
              [formControl]="row.controls.phone"
              [placeholder]="'enter_phone_number' | translate"
              [error]="isInvalid(row, 'phone')"
              (focusout)="row.controls.phone.markAsTouched()"
            />

            <app-input
              [formControl]="row.controls.first_name"
              [placeholder]="'first_name' | translate"
              [invalid]="isInvalid(row, 'first_name')"
              [showErrors]="false"
            />

            <app-input
              [formControl]="row.controls.last_name"
              [placeholder]="'last_name' | translate"
              [invalid]="isInvalid(row, 'last_name')"
              [showErrors]="false"
            />

            <app-select
              [formControl]="row.controls.group_id"
              [options]="groups"
              optionValue="id"
              optionLabel="name"
              [loading]="isGroupsLoading"
              [placeholder]="'choose_role' | translate"
              [error]="isInvalid(row, 'group_id')"
              [showErrors]="false"
              (focusout)="row.controls.group_id.markAsTouched()"
            />

            <button
              type="button"
              class="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:pointer-events-none disabled:opacity-30"
              [attr.aria-label]="'delete' | translate"
              [disabled]="isSubmitting || rows.length === 1"
              (click)="removeRow(i)"
            >
              <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">delete</app-icon>
            </button>
          </div>

          @if (rowErrors.get(row); as errorKey) {
            <div class="ml-11 flex items-center gap-2 text-sm text-red-600">
              <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">error_outline</app-icon>
              <span>{{ errorKey | translate }}</span>
            </div>
          }
        </div>
      }

      <button
        type="button"
        class="flex h-11 items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 text-sm font-medium text-gray-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 disabled:pointer-events-none disabled:opacity-40"
        [disabled]="isSubmitting || rows.length >= MAX_INVITES"
        (click)="addRow()"
      >
        <app-icon class="!h-5 !w-5 !text-[20px] !leading-5">add</app-icon>
        {{ 'add_employee' | translate }}
      </button>
    </div>
  </div>

  <footer class="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
    <app-button btnType="stroked" [disabled]="isSubmitting" (onChangeClick)="onCancel()">
      {{ 'cancel' | translate }}
    </app-button>
    <app-button [disabled]="isSubmitting" (onChangeClick)="onInvite()">
      @if (isSubmitting) {
        <app-loader size="tiny" strokeWidth="3" />
      } @else {
        {{ 'invite' | translate }} ({{ rows.length }})
      }
    </app-button>
  </footer>
</section>
